import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Package, ToggleLeft, ToggleRight } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

//import { GlobalFilterBar, FilterValues } from '../components/ui/GlobalFilterBar'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { FormError } from '../components/ui/FormError'
import { SearchFilter } from '../components/ui/SearchFilter'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog'

// Servicios
import { doctorsService } from '../services/doctors.service'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
// Define aquí cómo es el objeto que viene del Backend
interface Doctor {
    id: number
    full_name: string
    professional_license: string
    address_line1: string
    address_line2: string | null
    city: string
    state: string
    zip: string
    country: string
    is_active: boolean
}

// ─── 2. Esquema de Validación (Zod) ──────────────────────────────────────────
// Aquí configuras si un campo es obligatorio, cuántas letras mínimas, correos, etc.
const formSchema = z.object({
    full_name: z.string().min(1, 'El nombre es obligatorio').min(3, 'El nombre debe tener al menos 3 caracteres'),
    professional_license: z.string().min(1, 'La cédula es obligatoria').min(3, 'La cédula debe tener al menos 3 caracteres'),
    address_line1: z.string().min(1, 'La dirección es obligatoria').min(3, 'La dirección debe tener al menos 3 caracteres'),
    address_line2: z.string().nullable(),
    city: z.string().min(1, 'La ciudad es obligatoria').min(3, 'La ciudad debe tener al menos 3 caracteres'),
    state: z.string().min(1, 'El estado es obligatorio').min(3, 'El estado debe tener al menos 3 caracteres'),
    zip: z.string().min(1, 'El zip es obligatorio').min(4, 'El zip debe tener al menos 4 caracteres'),
    country: z.string().min(1, 'El país es obligatorio').min(2, 'El país debe tener al menos 2 caracteres'),
    is_active: z.boolean(),
})

// Extrae el tipo de dato para Typescript a partir del esquema Zod
type FormValidate = z.infer<typeof formSchema>

// ─── 3. Componente Principal ───────────────────────────────────────────────
export function Doctors() {
    // ─── Estados (Variables Reactivas) ───
    const [data, setData] = useState<Doctor[]>([])
    const [paginationMeta, setPaginationMeta] = useState<any>({ total: 0, from: 0, to: 0 })
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')
    const [refreshCount, setRefreshCount] = useState(0) // Usado para forzar que la tabla se recargue

    // Control de Modales
    const [showModal, setShowModal] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

    // Paginación
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 10

    //FILTROS componente global
    /*
    const [filters, setFilters] = useState<FilterValues>({
        search: '',
        full_name: '',
        professional_license: '',
        specialty: '',
        is_active: '',
    })
    */


    // ─── Formulario (React Hook Form) ───
    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm<FormValidate>({
        mode: 'onChange',
        resolver: zodResolver(formSchema as any),
        defaultValues: { full_name: '', professional_license: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', country: 'MX', is_active: true }
    })

    const isActiveValue = watch('is_active')

    // ─── Efecto: Cargar Datos de la API ───
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true)
            try {
                const response = await doctorsService.getItems({
                    search: searchQuery,
                    page: currentPage
                })

                // Ajustar según cómo tu backend devuelva el JSON:
                const items = response.data?.data || response.data || []
                setData(items)

                // Guardar metadatos de paginación del backend (Laravel)
                setPaginationMeta(response.data || response.meta || response || { total: 0, from: 0, to: 0 })
            } catch (error) {
                toast.error('Error al cargar los datos')
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [refreshCount, searchQuery, currentPage]) // Al cambiar estas dependencias, este bloque vuelve a ejecutarse

    // Los datos ya vienen filtrados y paginados del backend
    const paginatedData = data

    // ─── Funciones de Interfaz ───
    const openCreateModal = () => {
        setEditingId(null)
        reset({ full_name: '', professional_license: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', country: 'MX', is_active: true }) // Limpia el formulario
        setShowModal(true)
    }

    const openEditModal = (item: Doctor) => {
        setEditingId(item.id)
        reset({ full_name: item.full_name, professional_license: item.professional_license, address_line1: item.address_line1, address_line2: item.address_line2, city: item.city, state: item.state, zip: item.zip, country: item.country, is_active: item.is_active }) // Carga datos viejos
        setShowModal(true)
    }

    // ─── Funciones API (Guardar y Eliminar) ───
    const onSubmit = async (formData: FormValidate) => {
        try {
            if (editingId) {
                // Editar
                await doctorsService.updateItem(editingId, formData)
                toast.success('Registro actualizado exitosamente')
            } else {
                // Crear
                await doctorsService.createItem(formData)
                toast.success('Registro creado exitosamente')
            }
            setShowModal(false)
            setRefreshCount(prev => prev + 1) // Sumar 1 recarga la tabla automáticamente
        } catch (error) {
            toast.error('Error al guardar el registro')
        }
    }

    const handleStatusChange = async (id: number) => {
        try {
            await doctorsService.toggleStatus(id)
            toast.success('Estado actualizado exitosamente')
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al actualizar el estado')
        }
    }

    const executeDelete = async () => {
        if (!deleteConfirmId) return
        try {
            await doctorsService.deleteItem(deleteConfirmId)
            toast.success('Registro eliminado')
            setRefreshCount(prev => prev + 1)
        } catch (error) {
            toast.error('Error al eliminar')
        } finally {
            setDeleteConfirmId(null)
        }
    }

    // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
    return (
        <div className="space-y-6 p-6">

            {/* 1. Header y Buscador */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Doctores</h1>
                    <p className="mt-1 text-sm text-slate-600">Administra los doctores del sistema.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative w-full sm:w-64">
                        <SearchFilter
                            placeholder="Buscar..."
                            onSearch={(text) => {
                                setSearchQuery(text)
                                setCurrentPage(1) // Regresar a la primera página al buscar
                            }}
                        />
                    </div>
                    <Button onClick={openCreateModal} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                        <Plus className="mr-2 h-4 w-4" /> Nuevo Doctor
                    </Button>
                </div>
            </div>

            {/* 2. Tabla de Datos */}
            {isLoading ? (
                // Esqueleto: el número de 'columns' debe coincidir con tus columnas reales
                <TableSkeleton
                    headers={['Nombre', 'Cédula', 'Calle', 'Colonia', 'Ciudad', 'Estado', 'Código Postal', 'Acciones']}
                    columns={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    {/* Encabezados de Tabla */}
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Nombre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Cédula</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Calle</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Colonia</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Ciudad</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Estado</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Código Postal</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Estatus</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item) => (
                                        <tr key={item.id} className="hover:bg-slate-50">
                                            {/* Celdas de Datos */}
                                            <td className="px-6 py-4 font-medium text-slate-900">{item.full_name}</td>
                                            <td className="px-6 py-4 text-slate-700">{item.professional_license}</td>

                                            <td className="px-6 py-4 text-slate-700">{item.address_line1}</td>
                                            <td className="px-6 py-4 text-slate-700">{item.address_line2}</td>
                                            <td className="px-6 py-4 text-slate-700">{item.city}</td>
                                            <td className="px-6 py-4 text-slate-700">{item.state}</td>
                                            <td className="px-6 py-4 text-slate-700">{item.zip}</td>
                                            <td className="px-6 py-4">
                                                <Badge className={item.is_active ? 'bg-green-100 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>
                                                    {item.is_active ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => handleStatusChange(item.id)}
                                                        title={item.is_active ? 'Desactivar' : 'Activar'}
                                                        className={`rounded p-1.5 transition-colors ${item.is_active
                                                            ? 'text-green-600 hover:bg-green-50'
                                                            : 'text-slate-400 hover:bg-slate-100'
                                                            }`}
                                                    >
                                                        {item.is_active ? (
                                                            <ToggleRight className="h-5 w-5" />
                                                        ) : (
                                                            <ToggleLeft className="h-5 w-5" />
                                                        )}
                                                    </button>
                                                    <button onClick={() => openEditModal(item)} className="rounded p-1 hover:bg-slate-100" title="Editar">
                                                        <Edit className="h-4 w-4 text-slate-600" />
                                                    </button>
                                                    <button onClick={() => setDeleteConfirmId(item.id)} className="rounded p-1 hover:bg-red-50" title="Eliminar">
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        {/* Estado de "No hay registros" */}
                                        <td colSpan={6} className="px-6 py-12 text-center">
                                            <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron registros</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* 3. Paginación */}
                    {paginationMeta.total > 0 && (
                        <GlobalPaginator
                            totalItems={paginationMeta.total}
                            itemsPerPage={itemsPerPage}
                            currentPage={currentPage}
                            onPageChange={setCurrentPage}
                            from={paginationMeta.from}
                            to={paginationMeta.to}
                        />
                    )}
                </div>
            )}

            {/* 4. Modal Crear/Editar */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Editar Registro' : 'Nuevo Registro'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">

                        {/* Input Nombre */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Nombre <span className="text-red-500">*</span></label>
                            <Input {...register('full_name')} placeholder="Ingresa el nombre" className={errors.full_name ? 'border-red-500 ' : ''} />
                            {errors.full_name && <FormError message={errors.full_name.message} />}
                        </div>

                        {/* Input Cédula */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Cédula <span className="text-red-500">*</span></label>
                            <Input {...register('professional_license')} placeholder="Ingresa la cédula" className={errors.professional_license ? 'border-red-500 ' : ''} />
                            {errors.professional_license && <FormError message={errors.professional_license.message} />}
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Código Postal <span className="text-red-500">*</span></label>
                            <Input {...register('zip')} placeholder="Ingresa el código postal" className={errors.zip ? 'border-red-500 ' : ''} />
                            {errors.zip && <FormError message={errors.zip.message} />}
                        </div>
                        {/* Input Dirección */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Calle <span className="text-red-500">*</span></label>
                            <Input {...register('address_line1')} placeholder="Ingresa la calle" className={errors.address_line1 ? 'border-red-500 ' : ''} />
                            {errors.address_line1 && <FormError message={errors.address_line1.message} />}
                        </div>

                        {/* Input Colonia */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Colonia <span className="text-red-500">*</span></label>
                            <Input {...register('address_line2')} placeholder="Ingresa la colonia" className={errors.address_line2 ? 'border-red-500 ' : ''} />
                            {errors.address_line2 && <FormError message={errors.address_line2.message} />}
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Ciudad <span className="text-red-500">*</span></label>
                            <Input {...register('city')} placeholder="Ingresa la ciudad" className={errors.city ? 'border-red-500 ' : ''} />
                            {errors.city && <FormError message={errors.city.message} />}
                        </div>

                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">Estado <span className="text-red-500">*</span></label>
                            <Input {...register('state')} placeholder="Ingresa el estado" className={errors.state ? 'border-red-500 ' : ''} />
                            {errors.state && <FormError message={errors.state.message} />}
                        </div>



                        {/* Input Activo */}
                        <div className='flex items-center gap-2'>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={isActiveValue}
                                onClick={() => setValue('is_active', !isActiveValue)}
                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-green-500' : 'bg-slate-200'
                                    }`}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                                        }`}
                                />
                            </button>
                            <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                                Activo
                            </label>
                        </div>

                        <DialogFooter className="mt-6">
                            <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={isSubmitting} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                                {/* Loader discreto que se muestra mientras se guarda */}
                                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* 5. Modal Global de Confirmación para Eliminar */}
            <ConfirmationModal
                isOpen={deleteConfirmId !== null}
                onClose={() => setDeleteConfirmId(null)}
                onConfirm={executeDelete}
                preset="delete"
                itemName="registro"
            />

        </div>
    )
}
